import * as React from 'react';

import PropTypes from 'prop-types';

import { Radio, RadioGroup } from '@coursera/coursera-ui';

import { createOrganization } from 'bundles/internal-enterprise/utils/apiUtils';
import Button from 'bundles/internal-ui/components/buttons/Button';
import DropdownMenu from 'bundles/internal-ui/components/forms/DropdownMenu';
import TextBox from 'bundles/internal-ui/components/forms/TextBox';
import Modal from 'bundles/internal-ui/components/modal/Modal';

import _t from 'i18n!nls/internal-enterprise';

const LOGIN_METHOD_DATA = [
  { value: 'JWT_INVITE', label: 'Non-SSO' },
  { value: 'JWT', label: 'SSO - JWT' },
  { value: 'SAML', label: 'SSO - SAML' },
];

const ORG_TYPE_DATA = [
  { value: '', label: '' },
  { value: 'BUSINESS', label: 'Coursera for Business (C4B)' },
  { value: 'CAMPUS', label: 'Coursera for Campus (C4C)' },
  { value: 'GOVERNMENT', label: 'Coursera for Government (C4G)' },
];

type Props = {
  onSubmit: () => void;
  closeModal: () => void;
};

type State = {
  name: string;
  slug: string;
  entityId: string;
  emailDomain: string;
  loginMethod: string;
  organizationType: string;
  isReal?: boolean;
  hasError: boolean;
  isSubmitting: boolean;
};

export default class NewThirdPartyOrganization extends React.Component<Props, State> {
  static propTypes = {
    onSubmit: PropTypes.func,
    closeModal: PropTypes.func,
  };

  state: State = {
    name: '',
    slug: '',
    entityId: '',
    emailDomain: '',
    loginMethod: 'JWT_INVITE',
    organizationType: '',
    hasError: false,
    isSubmitting: false,
  };

  onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const { onSubmit } = this.props;
    const { loginMethod, entityId, emailDomain, slug, name, organizationType, isReal, isSubmitting } = this.state;

    // Prevent double submission
    if (isSubmitting) {
      return;
    }

    if (typeof isReal === 'undefined') {
      this.setState({ hasError: true });
      return;
    } else {
      this.setState({ hasError: false });
    }

    const formData: any = {
      entityId,
      emailDomain,
      slug,
      name,
      organizationType,
      loginMethod,
      isReal,
    };

    this.setState({ isSubmitting: true });

    try {
      await createOrganization(formData);
      onSubmit();
    } finally {
      this.setState({ isSubmitting: false });
    }
  };

  onUpdateIsRealOrg = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const isReal = value === 'true';
    this.setState({ isReal, hasError: false });
  };

  onUpdateField = (key: keyof State) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { value } = e.currentTarget;
    this.setState((state) => ({ ...state, [key]: value }));
  };

  render() {
    const { closeModal } = this.props;
    const { name, slug, entityId, emailDomain, isReal, hasError, isSubmitting } = this.state;
    return (
      <Modal
        title={_t('Create New Organization')}
        onRequestClose={closeModal}
        open
        scrollable
        actions={[
          {
            text: _t('Close'),
            onClick: closeModal,
            style: 'danger',
          },
        ]}
      >
        <form onSubmit={this.onSubmit}>
          <span>{_t('Is this a real or fake organization?')}</span>
          {hasError && (
            <span className="red-text help-block">
              {_t('Error: You must select if this is a real or fake organization')}
            </span>
          )}
          <RadioGroup rootClassName="m-t-2 m-b-2" onChange={this.onUpdateIsRealOrg} value={String(isReal)}>
            <Radio value="true">{_t('Real')}</Radio>
            <Radio value="false">{_t('Fake (used for demos and testing)')}</Radio>
          </RadioGroup>
          <p>
            {_t(
              'Note: Fake organizations are not included in financial reporting, product analytics, and PR metrics. The product experience remains unaffected.'
            )}
          </p>
          <TextBox
            placeholder="Name"
            label="Name"
            onUpdateField={this.onUpdateField('name')}
            currentValue={name}
            inputProps={{ required: true }}
          />
          <TextBox
            placeholder="Slug"
            label="Slug"
            onUpdateField={this.onUpdateField('slug')}
            currentValue={slug}
            inputProps={{ required: true }}
          />
          <TextBox
            placeholder="Entity ID"
            label="Entity ID"
            onUpdateField={this.onUpdateField('entityId')}
            currentValue={entityId}
          />
          <TextBox
            placeholder="Email Domain"
            label="Email Domain"
            onUpdateField={this.onUpdateField('emailDomain')}
            currentValue={emailDomain}
          />
          <DropdownMenu
            label="Organization Type"
            onChange={this.onUpdateField('organizationType')}
            options={ORG_TYPE_DATA}
            selectProps={{ required: true }}
          />
          <DropdownMenu
            label="Authentication Method"
            onChange={this.onUpdateField('loginMethod')}
            options={LOGIN_METHOD_DATA}
          />
          <p>
            Note: You can upload SAML metadata <em>after</em> creating the organization
          </p>
          <Button
            type="submit"
            btnStyle="success"
            text={isSubmitting ? _t('Submitting...') : _t('Submit')}
            disabled={isSubmitting}
          />
        </form>
      </Modal>
    );
  }
}
